/**
 * Translation Hook - Custom wrapper for react-i18next
 *
 * This hook provides a consistent interface for accessing translations
 * throughout the application. It wraps react-i18next's useTranslation
 * to provide a standardized API and future-proof against library changes.
 *
 * USAGE:
 * @example
 * // Using default namespace (common)
 * const { t } = useTranslate();
 * return <span>{t('cancel')}</span>;
 *
 * @example
 * // Using specific namespace
 * const { t } = useTranslate('session');
 * return <span>{t('start-round')}</span>;
 *
 * @example
 * // Cross-namespace reference
 * const { t } = useTranslate('session');
 * return <span>{t('common:cancel')}</span>;
 *
 * @example
 * // With interpolation
 * const { t } = useTranslate();
 * return <span>{t('welcome-user', { name: 'John' })}</span>;
 *
 * @example
 * // With pluralization
 * const { t } = useTranslate();
 * return <span>{t('shots-count', { count: 5 })}</span>;
 */
import {
  useTranslation,
  type UseTranslationOptions,
  type UseTranslationResponse,
} from "react-i18next";

/**
 * Custom translation hook
 *
 * Wraps react-i18next's useTranslation for consistent usage across the app.
 * The wrapper ensures we can easily migrate or modify i18n behavior
 * without changing every component.
 *
 * @param namespace - Optional namespace or array of namespaces
 * @param options - Optional i18next options
 * @returns UseTranslationResponse with t function and helpers
 *
 * @example
 * const { t, i18n } = useTranslate('session');
 */
export const useTranslate = <N extends string = "common">(
  namespace?: N | N[],
  options?: UseTranslationOptions<N>
): UseTranslationResponse<N, undefined> => {
  return useTranslation(namespace, options);
};

/**
 * Re-export the TFunction type for typing translation functions in props
 *
 * @example
 * interface ComponentProps {
 *   t: TranslatorFunction;
 * }
 */
export type { TFunction as TranslatorFunction } from "i18next";
